import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import { EventsMap } from '@noths/polaris-client-next-redux';
import { priceFormatter } from '@noths/polaris-client-utils';

import { selectDecoratedDeliveryOptions } from 'src/components/molecules/DeliveryBlock';
import { DeliveryOptionInfo } from 'src/components/molecules/DeliveryBlock/components/DeliveryBlock';
import { getDeliveryByEventLabel } from 'src/components/molecules/DeliveryEvent/components/DeliveryEvent';
import { selectCheapestDeliveryOptionPrice } from 'src/components/molecules/DeliveryEvent/selectors';
import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectProduct } from 'src/redux/selectors';
import { getDateDifferenceInDays } from 'src/utils';
import { requestDeliveryZoneOptions } from '../thunks';
import { DeliveryInfoEvent, DeliveryInfoEventUnavailableDelivery } from './events';

const getDeliveryByEventCost = (state: ReduxApplicationState) => {
  if (!state.product.deliveryByEvent?.arriveByEvent) {
    return 'not set';
  }

  const cost = selectCheapestDeliveryOptionPrice(state);

  if (cost == null) {
    return 'not set';
  }

  return cost || 'free';
};

const getMinMaxDeliveryDateInDays = ({
  exactDate,
  date,
  min,
  max,
}: DeliveryOptionInfo): {
  min: number;
  max: number;
} => {
  if (exactDate) {
    const daysFromToday = getDateDifferenceInDays(new Date(), new Date(date!));

    return { min: daysFromToday, max: daysFromToday };
  }

  return { min: min!, max: max! };
};

const getDeliveryMinMax = (deliveryOptions: DeliveryOptionInfo[]): { min: string; max: string } => {
  // Find the largest & smallest delivery estimate in days from the delivery types
  const minDays = deliveryOptions
    ? String(Math.min(...deliveryOptions.map((option) => getMinMaxDeliveryDateInDays(option).min)))
    : 'not set';

  const maxDays = deliveryOptions
    ? String(Math.max(...deliveryOptions.map((option) => getMinMaxDeliveryDateInDays(option).max)))
    : 'not set';

  return { min: minDays, max: maxDays };
};

export const GAEventsMap: EventsMap<ReduxApplicationState> = {
  [requestDeliveryZoneOptions.fulfilled.type]: (_, nextState) => {
    const countryDeliveryOptions = selectDecoratedDeliveryOptions(nextState);

    if (!countryDeliveryOptions.length) {
      const { selectedCountry, selectedZone } = nextState.deliveryZoneOptions;

      sendGAEvent<DeliveryInfoEventUnavailableDelivery>({
        event: 'custom_event',
        event_action: 'Delivery info',
        event_category: 'Delivery & returns',
        event_label: `${selectedCountry} - ${selectedZone}`,
        delivery: {
          delivery_by_event: 'not set',
          delivery_by_event_cost: 'not set',
          max_delivery_time: 'not set',
          min_delivery_time: 'not set',
          options: 'none',
        },
      });
    } else {
      const product = selectProduct(nextState);
      const countryDeliveryOptionCodes = countryDeliveryOptions
        .map((opt) => opt.optionCode)
        .join(', ');

      const { min: deliveryOptionMin, max: deliveryOptionMax } = getDeliveryMinMax(
        countryDeliveryOptions,
      );

      const currency = nextState.userConfiguration.currencyCode || nextState.product.price.currency;
      const deliveryByEventCheapestPrice = getDeliveryByEventCost(nextState);
      const deliveryByEvent = product.deliveryByEvent?.arriveByEvent
        ? getDeliveryByEventLabel(product.deliveryByEvent.eventName).replace(' for', '')
        : 'not set';

      sendGAEvent<DeliveryInfoEvent>({
        event: 'custom_event',
        event_action: 'Delivery info',
        event_category: 'Delivery & returns',
        event_label: countryDeliveryOptionCodes,
        delivery: {
          options: countryDeliveryOptionCodes,
          min_delivery_time: deliveryOptionMin,
          max_delivery_time: deliveryOptionMax,
          delivery_by_event: deliveryByEvent,
          delivery_by_event_cost:
            typeof deliveryByEventCheapestPrice === 'number'
              ? priceFormatter(currency, deliveryByEventCheapestPrice)
              : deliveryByEventCheapestPrice,
        },
      });
    }
  },
};
